import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { ChevronDown, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { CountryFlag } from "@/components/ui/country-flag";
import { Button } from "@/components/ui/button";
import type { LeagueInfo, LeagueGender } from "@/hooks/useActiveLeagues";
import { filterLeaguesByGender } from "@/hooks/useActiveLeagues";

// Nation metadata by code prefix
const NATION_CONFIG: Record<string, {
  name: string;
  order: number;
}> = {
  'ENG': {
    name: 'England',
    order: 1
  },
  'ESP': {
    name: 'Spain',
    order: 2
  },
  'GER': {
    name: 'Germany',
    order: 3
  },
  'ITA': {
    name: 'Italy',
    order: 4
  },
  'FRA': {
    name: 'France',
    order: 5
  },
  'NED': {
    name: 'Netherlands',
    order: 6
  },
  'POR': {
    name: 'Portugal',
    order: 7
  },
  'BEL': {
    name: 'Belgium',
    order: 8
  },
  'SCO': {
    name: 'Scotland',
    order: 9
  },
  'TUR': {
    name: 'Turkey',
    order: 10
  },
  'AUT': {
    name: 'Austria',
    order: 11
  },
  'SUI': {
    name: 'Switzerland',
    order: 12
  },
  'NOR': {
    name: 'Norway',
    order: 13
  },
  'SWE': {
    name: 'Sweden',
    order: 14
  },
  'DEN': {
    name: 'Denmark',
    order: 15
  },
  'POL': {
    name: 'Poland',
    order: 16
  },
  'CRO': {
    name: 'Croatia',
    order: 17
  },
  'USA': {
    name: 'USA',
    order: 18
  },
  'BRA': {
    name: 'Brazil',
    order: 19
  },
  'MEX': {
    name: 'Mexico',
    order: 20
  },
  'ARG': {
    name: 'Argentina',
    order: 21
  },
  'GRE': {
    name: 'Greece',
    order: 22
  },
  'RUS': {
    name: 'Russia',
    order: 23
  },
  'UKR': {
    name: 'Ukraine',
    order: 24
  },
  'CZE': {
    name: 'Czech Republic',
    order: 25
  }
};

// Big 5 nation codes - these get prominent display
const BIG_5_NATIONS = ['ENG', 'ESP', 'GER', 'ITA', 'FRA'];
interface NationGroup {
  nationCode: string;
  name: string;
  leagues: LeagueInfo[];
  order: number;
}
interface LeagueSelectorCardProps {
  leagues: LeagueInfo[];
  activeLeague: string;
  onLeagueChange: (code: string) => void;
}
interface LeagueButtonProps {
  league: LeagueInfo;
  isActive: boolean;
  onClick: () => void;
  compact?: boolean;
}
const LeagueButton = ({
  league,
  isActive,
  onClick,
  compact = false
}: LeagueButtonProps) => {
  return <button onClick={onClick} className={cn("text-left transition-all rounded-md", compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm", "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1", isActive ? "bg-primary/10 text-primary font-medium border border-primary/30 hover:bg-primary hover:text-primary-foreground" : "text-foreground hover:text-foreground hover:bg-secondary border border-transparent")} aria-label={`Select ${league.displayName}`} aria-pressed={isActive}>
      {league.displayName}
    </button>;
};
const getNationCode = (leagueCode: string): string => leagueCode.split('_')[0];
const groupLeaguesByNation = (leagues: LeagueInfo[]): NationGroup[] => {
  const groups = new Map<string, LeagueInfo[]>();
  leagues.forEach(league => {
    const nationCode = getNationCode(league.code);
    if (!groups.has(nationCode)) {
      groups.set(nationCode, []);
    }
    groups.get(nationCode)!.push(league);
  });
  return Array.from(groups.entries()).map(([code, leagueList]) => ({
    nationCode: code,
    name: NATION_CONFIG[code]?.name || code,
    leagues: leagueList.sort((a, b) => {
      // Sort by tier if available, otherwise alphabetically
      const tierA = a.tier === 'top' ? 1 : a.tier === 'second' ? 2 : 3;
      const tierB = b.tier === 'top' ? 1 : b.tier === 'second' ? 2 : 3;
      if (tierA !== tierB) return tierA - tierB;
      return a.displayName.localeCompare(b.displayName);
    }),
    order: NATION_CONFIG[code]?.order || 99
  })).sort((a, b) => a.order - b.order);
};
const LeagueSelectorCard = ({
  leagues,
  activeLeague,
  onLeagueChange
}: LeagueSelectorCardProps) => {
  const [otherOpen, setOtherOpen] = useState(false);
  const activeLeagueGender: LeagueGender =
    leagues.find(l => l.code === activeLeague)?.gender || 'men';
  const [gender, setGender] = useState<LeagueGender>(activeLeagueGender);
  const hasWomens = leagues.some(l => l.gender === 'women');
  const genderLeagues = filterLeaguesByGender(leagues, gender);

  const switchGender = (next: LeagueGender) => {
    setGender(next);
    const list = filterLeaguesByGender(leagues, next);
    if (list.length > 0 && !list.some(l => l.code === activeLeague)) {
      onLeagueChange(list[0].code);
    }
  };

  const genderToggle = hasWomens ? (
    <div className="inline-flex rounded-md border border-border p-0.5" role="group" aria-label="Choose men's or women's football">
      {(['men', 'women'] as LeagueGender[]).map(g => (
        <button
          key={g}
          onClick={() => switchGender(g)}
          aria-pressed={gender === g}
          className={cn(
            "px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded",
            gender === g ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {g === 'men' ? "Men's" : "Women's"}
        </button>
      ))}
    </div>
  ) : null;

  const nationGroups = groupLeaguesByNation(genderLeagues);
  const big5Groups = nationGroups.filter(g => BIG_5_NATIONS.includes(g.nationCode));
  const otherGroups = nationGroups.filter(g => !BIG_5_NATIONS.includes(g.nationCode));

  // Check if active league is in "other" section to auto-expand
  const activeNation = getNationCode(activeLeague);
  const activeInOther = !BIG_5_NATIONS.includes(activeNation);
  return <Card className="p-2 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-3 md:mb-4">
        <h2 className="hidden md:block text-lg font-semibold text-foreground">Select a League</h2>
        {genderToggle && <div className="ml-auto">{genderToggle}</div>}
      </div>
      
      {/* Desktop: Nation grid layout */}
      <div className="hidden md:block space-y-4">
        {/* Big 5 Nations - 3 column grid */}
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-4">
          {big5Groups.map(group => <div key={group.nationCode} className="space-y-2">
              <div className="flex items-center gap-2">
                <CountryFlag code={group.nationCode} size="lg" />
                <span className="text-sm font-medium text-muted-foreground">
                  {group.name}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                {group.leagues.map(league => <LeagueButton key={league.code} league={league} isActive={activeLeague === league.code} onClick={() => onLeagueChange(league.code)} />)}
              </div>
            </div>)}
        </div>

        {/* Other Leagues - Collapsible */}
        {otherGroups.length > 0 && <Collapsible open={otherOpen || activeInOther} onOpenChange={setOtherOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full py-2 border-t border-border pt-4">
              {otherOpen || activeInOther ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="uppercase tracking-wide text-xs">Other Leagues</span>
              <span className="text-xs text-muted-foreground/70">
                ({otherGroups.reduce((acc, g) => acc + g.leagues.length, 0)})
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
                {otherGroups.map(group => <div key={group.nationCode} className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <CountryFlag code={group.nationCode} size="md" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {group.name}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {group.leagues.map(league => <LeagueButton key={league.code} league={league} isActive={activeLeague === league.code} onClick={() => onLeagueChange(league.code)} compact />)}
                    </div>
                  </div>)}
              </div>
            </CollapsibleContent>
          </Collapsible>}

        {gender === 'women' && genderLeagues.length < 2 && (
          <p className="text-xs text-muted-foreground">More women's leagues coming as coverage expands.</p>
        )}
      </div>

      {/* Mobile: Bottom sheet drawer */}
      <div className="md:hidden">
        <Drawer>
          <DrawerTrigger asChild>
            <Button variant="outline" className="w-full flex justify-between items-center">
              <div className="flex items-center gap-2">
                <CountryFlag code={activeNation} size="sm" />
                <span className="text-sm font-medium">
                  {leagues.find(l => l.code === activeLeague)?.displayName || activeLeague}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[80vh]">
            <DrawerHeader>
              <DrawerTitle>Select a League</DrawerTitle>
              {genderToggle && <div className="mt-2 flex justify-center">{genderToggle}</div>}
            </DrawerHeader>
            <div className="overflow-y-auto px-4 pb-6 space-y-5">
              {big5Groups.map(group => (
                <div key={group.nationCode} className="space-y-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CountryFlag code={group.nationCode} size="md" />
                    <span className="text-sm font-medium text-muted-foreground">{group.name}</span>
                  </div>
                  {group.leagues.map(league => (
                    <DrawerTrigger key={league.code} asChild>
                      <button
                        onClick={() => onLeagueChange(league.code)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between",
                          "hover:bg-accent transition-colors",
                          activeLeague === league.code
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-foreground"
                        )}
                      >
                        {league.displayName}
                        {activeLeague === league.code && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    </DrawerTrigger>
                  ))}
                </div>
              ))}

              {otherGroups.length > 0 && (
                <>
                  <div className="border-t border-border pt-4">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Other Leagues</span>
                  </div>
                  {otherGroups.map(group => (
                    <div key={group.nationCode} className="space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CountryFlag code={group.nationCode} size="md" />
                        <span className="text-sm font-medium text-muted-foreground">{group.name}</span>
                      </div>
                      {group.leagues.map(league => (
                        <DrawerTrigger key={league.code} asChild>
                          <button
                            onClick={() => onLeagueChange(league.code)}
                            className={cn(
                              "w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between",
                              "hover:bg-accent transition-colors",
                              activeLeague === league.code
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-foreground"
                            )}
                          >
                            {league.displayName}
                            {activeLeague === league.code && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        </DrawerTrigger>
                      ))}
                    </div>
                  ))}
                </>
              )}

              {gender === 'women' && genderLeagues.length < 2 && (
                <p className="text-xs text-muted-foreground pt-1">
                  More women's leagues coming as coverage expands.
                </p>
              )}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </Card>;
};
export default LeagueSelectorCard;